import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useAsync } from 'react-use';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import TypedOption from 'components/form/TypedOption.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';

import { getCategories } from 'salon/actions';

import CN from './Filter.module.scss';
const L = logger('ServicesFilter');

const ServicesFilter = ({ getCategories, filtered, onFilter }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();

  const [selected, setSelected] = useState(filtered);
  const [selectedCategories, setSelectedCategories] = useState(asArray());

  const { value: categories, loading: isLoading } = useAsync(async () => {
    const result = await getCategories(salonId);
    return result?.json || [];
  }, [getCategories, salonId]);

  const { val: isRendered, tip: toggle, nay: close } = useTip(false);

  useEffect(() => {
    const expandedCategories = categories?.filter((cat) => cat.services.some((service) => filtered.includes(service.id))).map((cat) => cat.id) || [];
    setSelected(filtered);
    setSelectedCategories(expandedCategories);
  }, [categories, filtered]);

  const selectedServicesCount = selected.length;

  const handleToggleCategory = useCallback(
    (cat) => {
      const isOpen = selectedCategories.includes(cat.id);

      if (isOpen) {
        // if it is open -> remove the cat id  from isCatExpanded and it becomes closed and
        // remove all services from selectedServices so that they are not selected
        const newSelectedCategories = selectedCategories.filter((id) => id !== cat.id);
        const newSelected = selected.filter((id) => !cat.services.some((service) => service.id === id));

        setSelectedCategories(newSelectedCategories);
        setSelected(newSelected);
      } else {
        // open cat and add services as selected
        const newSelectedCategories = [...selectedCategories, cat.id];

        const allServiceIds = cat.services.map((s) => s.id);
        const newSelected = [...selected];

        for (const id of allServiceIds) {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        }

        setSelectedCategories(newSelectedCategories);
        setSelected(newSelected);
      }
    },
    [selectedCategories, selected, setSelectedCategories, setSelected],
  );

  const handleToggleService = useCallback(
    (id) => {
      const newSelected = selected.includes(id) ? selected.filter((sId) => sId !== id) : [...selected, id];
      setSelected(newSelected);
    },
    [setSelected, selected],
  );

  const handleApply = useCallback(() => {
    call('ServicesFilter.onFilter', onFilter, selected);
    close();
  }, [close, onFilter, selected]);

  const handleClearAll = useCallback(() => {
    onFilter(asArray());
    setSelected(asArray());
    setSelectedCategories(asArray());
  }, [onFilter, setSelected, setSelectedCategories]);

  L.debug('() selected services ID', selected);

  return (
    <div
      data-bem="ServicesFilter"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div
        onClick={toggle}
        className={cn({
          [CN.filterButton]: true,
          [CN.isSelected]: selectedServicesCount > 0,
        })}
      >
        {selectedServicesCount > 0 && selectedServicesCount} {t('clientsEmailPage.servicesFilter.placeholder')}
        <Icon variant="arrowTriangle" width="xs" />
      </div>

      {isRendered && (
        <DropdownFoldout width="lg" position="bottom" onClickOutside={close}>
          <DropdownFoldout.Header>
            <div className={CN.dropdownTitle}>{t('clientsEmailPage.servicesFilter.title')}</div>
          </DropdownFoldout.Header>

          <DropdownFoldout.Body>
            <div className={CN.dropdownContent}>
              {!isLoading && categories?.length > 0 && (
                <div className={CN.dropdownContent}>
                  {categories.map((cat) => {
                    const isExpanded = selectedCategories.includes(cat.id);
                    return (
                      <div key={cat.id} className={CN.categoryGroup}>
                        <div className={CN.categoryName} onClick={() => handleToggleCategory(cat)}>
                          {cat.name}
                          <Toggler tag="ServicesFilter__cat" onClick={alwaysNoop} selected={isExpanded} hideLabel />
                        </div>

                        {isExpanded &&
                          cat.services.map((s) => (
                            <div key={s.id} className={CN.serviceItem}>
                              {s.name}
                              <TypedOption tag="ServicesFilter__select--service" type="simple-checkbox" checked={selected.includes(s.id)} onCheck={() => handleToggleService(s.id)} />
                            </div>
                          ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </DropdownFoldout.Body>

          <DropdownFoldout.Footer>
            <div className={CN.dropdownFooterContainer}>
              <Button variant="outline" onClick={handleClearAll} tag="ServicesFilter__clear--all">
                {t('common.buttons.clearAll').toUpperCase()}
              </Button>
              <Button variant="primary" onClick={handleApply} tag="ServicesFilter__done">
                {t('common.buttons.apply').toUpperCase()}
              </Button>
            </div>
          </DropdownFoldout.Footer>
        </DropdownFoldout>
      )}
    </div>
  );
};

const ServicesFilterHoc = connect(
  null,
  mapActionsToDispatchToProps({
    getCategories,
  }),
)(ServicesFilter);

export default ServicesFilterHoc;
